/**
 * EventBus - Simple pub/sub event system for decoupling modules
 *
 * This allows different parts of the game to communicate without
 * directly referencing each other. For example:
 *
 *   // In DriveStateMachine:
 *   eventBus.emit('speed-changed', { speed: 45, isMaxSpeed: false });
 *
 *   // In GameUI:
 *   eventBus.on('speed-changed', (data) => {
 *       this.updateSpeedDisplay(data.speed);
 *   });
 *
 * Benefits:
 * - Modules don't need direct references to each other
 * - Easy to add new listeners without modifying emitters
 * - Simplifies testing (can mock events)
 * - Clear communication contracts via event names
 *
 * Usage:
 *   import { eventBus } from './src/utils/EventBus.js';
 *
 *   // Subscribe to an event
 *   const unsubscribe = eventBus.on('event-name', (data) => { ... });
 *
 *   // Emit an event
 *   eventBus.emit('event-name', { someData: 'value' });
 *
 *   // Unsubscribe when done
 *   unsubscribe();
 *
 *   // Or subscribe to an event only once
 *   eventBus.once('event-name', (data) => { ... });
 */

class EventBus {
    constructor() {
        // Map of event names to arrays of listener functions
        this.listeners = new Map();
    }

    /**
     * Subscribe to an event
     * @param {string} eventName - Name of the event to listen for
     * @param {Function} callback - Function to call when event is emitted
     * @returns {Function} Unsubscribe function - call this to remove the listener
     */
    on(eventName, callback) {
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, []);
        }

        this.listeners.get(eventName).push(callback);

        // Return unsubscribe function for easy cleanup
        return () => {
            this.off(eventName, callback);
        };
    }

    /**
     * Subscribe to an event, but only fire once
     * @param {string} eventName - Name of the event to listen for
     * @param {Function} callback - Function to call when event is emitted
     * @returns {Function} Unsubscribe function
     */
    once(eventName, callback) {
        const onceCallback = (data) => {
            this.off(eventName, onceCallback);
            callback(data);
        };

        return this.on(eventName, onceCallback);
    }

    /**
     * Unsubscribe from an event
     * @param {string} eventName - Name of the event
     * @param {Function} callback - The specific callback to remove
     */
    off(eventName, callback) {
        if (!this.listeners.has(eventName)) {
            return;
        }

        const callbacks = this.listeners.get(eventName);
        const index = callbacks.indexOf(callback);

        if (index !== -1) {
            callbacks.splice(index, 1);
        }

        // Clean up empty listener arrays
        if (callbacks.length === 0) {
            this.listeners.delete(eventName);
        }
    }

    /**
     * Emit an event to all subscribers
     * @param {string} eventName - Name of the event to emit
     * @param {*} data - Data to pass to listeners (optional)
     */
    emit(eventName, data) {
        const callbacks = this.listeners.get(eventName);
        if (!callbacks || callbacks.length === 0) {
            return;
        }

        // Fast path: single listener (common case), no copy needed
        if (callbacks.length === 1) {
            try {
                callbacks[0](data);
            } catch (error) {
                console.error(`Error in event listener for "${eventName}":`, error);
            }
            return;
        }

        // Multiple listeners: iterate by index to handle removals during iteration
        // This avoids array copy while still being safe
        const len = callbacks.length;
        for (let i = 0; i < len; i++) {
            const callback = callbacks[i];
            if (callback) { // Check in case it was removed during iteration
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in event listener for "${eventName}":`, error);
                }
            }
        }
    }

    /**
     * Remove all listeners for an event, or all listeners entirely
     * @param {string} [eventName] - If provided, only clear listeners for this event
     */
    clear(eventName) {
        if (eventName) {
            this.listeners.delete(eventName);
        } else {
            this.listeners.clear();
        }
    }

    /**
     * Get the number of listeners for an event
     * @param {string} eventName - Name of the event
     * @returns {number} Number of listeners
     */
    listenerCount(eventName) {
        if (!this.listeners.has(eventName)) {
            return 0;
        }
        return this.listeners.get(eventName).length;
    }

    /**
     * Check if there are any listeners for an event
     * @param {string} eventName - Name of the event
     * @returns {boolean} True if there are listeners
     */
    hasListeners(eventName) {
        return this.listenerCount(eventName) > 0;
    }
}

// =============================================================================
// STANDARD EVENT NAMES
// =============================================================================
// Define standard event names here for consistency and discoverability.
// This helps prevent typos and makes it clear what events are available.

export const GameEvents = {
    // Speed/driving events
    SPEED_CHANGED: 'speed-changed',           // { speed, isMaxSpeed, isOverboost }
    MAX_SPEED_REACHED: 'max-speed-reached',   // { speed }
    AUTOPILOT_STARTED: 'autopilot-started',   // { duration }
    AUTOPILOT_ENDED: 'autopilot-ended',       // {}
    MOMENTUM_CHANGED: 'momentum-changed',     // { momentum, wasLost }

    // Miles/progression events
    MILES_CHANGED: 'miles-changed',           // { miles, totalMiles }
    MILESTONE_REACHED: 'milestone-reached',   // { milestone, packsAvailable }

    // Purchase events
    UPGRADE_PURCHASED: 'upgrade-purchased',   // { type, level, cost }
    CAR_PURCHASED: 'car-purchased',           // { carId }
    PACK_OPENED: 'pack-opened',               // { milestone, packQuality, wavedustEarned, itemCount }
    PACK_PURCHASED: 'pack-purchased',         // { milestone }
    WAVE_SCRAPPED: 'wave-scrapped',           // { milestone }

    // Prestige events
    PRESTIGE_PERFORMED: 'prestige-performed', // { level, selectedSkill, bonusGained }

    // Item events
    ITEM_EQUIPPED: 'item-equipped',           // { item, slot }
    ITEM_SELECTED: 'item-selected',           // { milestone, item }
    ITEM_UNEQUIPPED: 'item-unequipped',       // { item, slot }
    EFFECTS_RECALCULATED: 'effects-recalculated', // { effects }

    // Time events
    TIME_PHASE_CHANGED: 'time-phase-changed', // { phase, paletteIndex }
    GAME_TIME_UPDATED: 'game-time-updated',   // { gameTime, timeString }

    // UI events
    DRIVE_BUTTON_CLICKED: 'drive-button-clicked', // {}
    DRIVE_BUTTON_HELD: 'drive-button-held',       // { duration }
    MODAL_OPENED: 'modal-opened',                 // { modalName }
    MODAL_CLOSED: 'modal-closed',                 // { modalName }

    // Audio events
    MUSIC_PHASE_CHANGED: 'music-phase-changed', // { phase }

    // Biome events
    BIOME_CHANGED: 'biome-changed',           // { biomeName, biomeIndex }

    // Game state events
    GAME_STARTED: 'game-started',             // {}
    GAME_SAVED: 'game-saved',                 // {}
    GAME_LOADED: 'game-loaded',               // {}
};

// Create and export a singleton instance
// This ensures all modules share the same event bus
export const eventBus = new EventBus();

// Also export the class for testing or creating isolated instances
export { EventBus };
